/**
 * Playback for the sounds the back office uploaded.
 *
 * There are no file names in this file, and that is the point. The internal back
 * office uploads a sound and maps it to a game event
 * (backoffice-mimarisi.md A.4); the client asks `/api/v1/audio/manifest` which
 * event plays what, and calls `play('round.crash')`. Replacing the explosion is a
 * form somebody fills in, not a release.
 *
 * Three things this has to get right, and each of them is a way for game audio to
 * be worse than no audio at all:
 *
 *  1. **Autoplay.** A browser will not start audio before the player has
 *     interacted with the page, and a rejected `play()` is an unhandled promise
 *     rejection in the console every round. So nothing plays until the first real
 *     gesture, and the background loop starts there rather than on load.
 *  2. **Overlap.** Two stakes cash out in the same second, and one `Audio`
 *     element can only be at one position. Effects are played on a small pool of
 *     clones; music is a single looping element.
 *  3. **The mute switches are the account's.** `audio/settings.ts` already owns
 *     that, and it is the only thing that owns it -- this module asks, and
 *     re-reads on every change, rather than keeping a copy.
 *
 * A missing manifest, a 404 on a file, a codec the browser dislikes: all of them
 * end with silence and one console warning. Audio is decoration in a betting game
 * and must never be able to stop a round from being playable.
 */

import { isMusicEnabled, isSoundEnabled, onAudioChange } from './settings';

/** The event codes migration 0014 defines. Kept as a type so a typo is caught. */
export type AudioEventCode =
  | 'music.background'
  | 'round.betting'
  | 'round.start'
  | 'round.punch'
  | 'round.crash'
  | 'bet.placed'
  | 'bet.cashout'
  | 'countdown.tick';

interface ManifestEntry {
  event: string;
  url: string;
  volumePct: number;
  loops: boolean;
  channel: 'sound' | 'music';
}

interface Voice {
  url: string;
  volume: number;
  loops: boolean;
  channel: 'sound' | 'music';
  /** The element for a looping track; effects clone a template per play. */
  element: HTMLAudioElement;
  /** A small ring of clones, so two cash-outs in the same second both sound. */
  pool: HTMLAudioElement[];
  next: number;
}

const POOL_SIZE = 3;

/**
 * The shortest gap between two plays of the same effect.
 *
 * The punch fires off the animation's impact frame, and at a high multiplier the
 * animation speeds up until the impacts are a few dozen milliseconds apart. Left
 * alone that is not a rhythm, it is a buzz -- so a repeat inside this window is
 * dropped rather than layered.
 */
const MIN_REPEAT_MS = 90;

const voices = new Map<string, Voice>();
const lastPlayed = new Map<string, number>();

let unlocked = false;
let loaded = false;

/**
 * Loads the manifest. Safe to call more than once; the second call is a no-op.
 *
 * It does not wait for the files: the elements are created with `preload =
 * 'auto'` and the browser fetches them while the player is still reading the
 * betting panel. A sound that has not arrived by the time its event fires is
 * simply silent that once.
 */
export async function loadAudio(): Promise<void> {
  if (loaded) return;
  loaded = true;

  try {
    const response = await fetch('/api/v1/audio/manifest', { credentials: 'same-origin' });
    if (!response.ok) return;

    const body = (await response.json()) as { audio?: ManifestEntry[] };
    for (const entry of body.audio ?? []) {
      if (!entry.url) continue;

      const element = new Audio(entry.url);
      element.preload = 'auto';
      element.loop = entry.loops;
      const volume = Math.min(1, Math.max(0, (entry.volumePct ?? 100) / 100));
      element.volume = volume;

      const pool: HTMLAudioElement[] = [];
      if (!entry.loops) {
        for (let i = 0; i < POOL_SIZE; i += 1) {
          const clone = new Audio(entry.url);
          clone.preload = 'auto';
          clone.volume = volume;
          pool.push(clone);
        }
      }

      voices.set(entry.event, {
        url: entry.url,
        volume,
        loops: entry.loops,
        channel: entry.channel === 'music' ? 'music' : 'sound',
        element,
        pool,
        next: 0,
      });
    }

    applySwitches();
  } catch {
    // Offline, blocked by an extension, or a manifest we could not parse. The
    // game is unaffected; there is just nothing to play.
  }
}

/**
 * Arms playback on the first genuine gesture.
 *
 * `pointerdown` and `keydown` on the window, once. The gate is the browser's, not
 * ours: calling `play()` before a gesture is rejected, and doing it on every round
 * would fill the console with rejections that look like a bug in the game.
 */
export function armAudio(): void {
  if (unlocked) return;

  const unlock = (): void => {
    if (unlocked) return;
    unlocked = true;
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    window.removeEventListener('touchend', unlock);
    startMusic();
  };

  // `touchend` as well as `pointerdown`, because iOS treats a touch that turns
  // into a scroll as not-a-gesture and the drawer is scrollable.
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('touchend', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true });
}

/** Plays one event, if a sound is mapped to it and its channel is on. */
export function play(event: AudioEventCode): void {
  const voice = voices.get(event);
  if (!voice || !unlocked || !channelOn(voice.channel)) return;

  const now = Date.now();
  if (now - (lastPlayed.get(event) ?? 0) < MIN_REPEAT_MS) return;
  lastPlayed.set(event, now);

  if (voice.loops) {
    void voice.element.play().catch(() => undefined);
    return;
  }

  // Round-robin through the pool so overlapping plays do not cut each other off.
  const element = voice.pool[voice.next % voice.pool.length] ?? voice.element;
  voice.next += 1;
  try {
    element.currentTime = 0;
  } catch {
    // Some browsers throw if the metadata has not loaded yet; playing from
    // wherever it is is better than not playing.
  }
  void element.play().catch(() => undefined);
}

function channelOn(channel: 'sound' | 'music'): boolean {
  return channel === 'music' ? isMusicEnabled() : isSoundEnabled();
}

function startMusic(): void {
  const music = voices.get('music.background');
  if (!music || !isMusicEnabled()) return;
  void music.element.play().catch(() => undefined);
}

/**
 * Applies the account's switches. Registered with `audio/settings.ts`, which is
 * the single source of truth for them, so a player muting the music on their
 * phone silences it here without this module storing anything.
 */
function applySwitches(): void {
  const music = voices.get('music.background');
  if (!music) return;

  if (isMusicEnabled() && unlocked) {
    void music.element.play().catch(() => undefined);
  } else {
    music.element.pause();
  }
}

onAudioChange(() => applySwitches());
